import { useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@mui/material/Button";
import { useStore } from "../../helpers";
import { safe_get } from "../../report-visuals";
import { SideOverlay } from "../../components";
import { FTPBreadCrumbs } from "../../components-new";
import PageShell from "../../layouts/PageShell";
import { Task, TaskFilters, TaskStatusType } from "./types";
import { TaskTable } from "./TaskTable";
import { TaskCreate } from "./task-create/TaskCreate";
import { TaskView } from "./task-view/TaskView";
import { TaskEdit } from "./task-edit/TaskEdit";
import { InlineFilters } from "./filters/InlineFilters";
import { TaskManagerErrorBoundary } from "./components/TaskManagerErrorBoundary";
import AssignTaskForm from "../task-manager/task-list-components/AssignTaskForm";
import BoothMapView from "../task-manager/task-list-components/booth-map-view";
import { getSurveysFromStorage } from "./utils/localStorageUtils";
import { STATUS_TABS } from "./utils/constants";
import { DEFAULT_PAGE_SIZE } from "./utils/constants";
import { taskQueryKeys } from "./utils/queryKeys";
import { toJS } from "mobx";

export const TaskManager = observer(() => {
    const { projectid } = useParams();
    const queryClient = useQueryClient();

    const { taskManagerStore, projectStore } = useStore();
    const { task_filters, show_assign_task_modal, toggle_assign_task_modal, active_task, show_booth_map } =
        taskManagerStore;

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

    // Drawer state - separate for view and edit
    const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [geoJsonUrl, setGeoJsonUrl] = useState<String | null>(null);
    useEffect(() => {
        // console.log(toJS(selectedTask), "sele");
        const boothMapUrl = safe_get(toJS(selectedTask), "node.booth_map", "");
        // console.log("booth map url", boothMapUrl);
        setGeoJsonUrl(boothMapUrl.replace("https://demo.rajneethi.com/gj.html?path=", ""));
    }, [selectedTask]);

    // Create drawer state
    const [createDrawerOpen, setCreateDrawerOpen] = useState(false);

    // Breadcrumbs
    const crumbs = [
        { name: "Dashboard", href: `/project/${projectid}`, current: false },
        { name: "Task Manager", href: "#", current: true },
    ];

    // Initialize data for surveys (from local storage)
    useEffect(() => {
        const surveys = getSurveysFromStorage();
        taskManagerStore.set_surveys(surveys);
    }, [projectid, taskManagerStore]);

    // Reset on project change
    useEffect(() => {
        taskManagerStore.set_task_edit_modal(false);
        taskManagerStore.set_task_view_modal(false);
        taskManagerStore.set_task_list_query("");
        setCurrentPage(1);
        setPageSize(DEFAULT_PAGE_SIZE);
        setViewDrawerOpen(false);
        setEditDrawerOpen(false);
        setSelectedTask(null);
    }, [projectid]);

    // Centralized refresh handler to avoid duplication
    const handleRefresh = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: taskQueryKeys.list(task_filters as TaskFilters, currentPage, pageSize),
        });
    }, [queryClient, task_filters, currentPage, pageSize]);

    // React Query: task list - uses query key factory
    const taskListQuery = useQuery(
        taskQueryKeys.list(task_filters as TaskFilters, currentPage, pageSize),
        async () => {
            taskManagerStore.update_tm_store_prop("tasklist_current_page_number", currentPage);
            taskManagerStore.update_tm_store_prop("task_count_per_page", pageSize);
            const apiResponse = await taskManagerStore.get_task_list(projectid);
            const result = {
                tasks: taskManagerStore.filtered_task_list,
                total: taskManagerStore.task_list_total,
                status: apiResponse?.status || taskManagerStore.task_summary?.status || null,
            };
            return result;
        },
        {
            enabled: !!projectid,
            keepPreviousData: true,
        }
    );

    // React Query: users - uses query key factory
    useQuery(
        taskQueryKeys.users(projectid),
        async () => {
            const payload = {
                projectid: parseInt(projectid as string),
                tags: [],
                query: "",
                limit: 500,
                offset: 0,
            };
            const res = await projectStore.getAllUsers(payload);
            if (res.status !== 200) {
                throw new Error("Failed to load users");
            }
            return safe_get(res.data, "users", []);
        },
        {
            enabled: !!projectid,
            onSuccess: (users) => {
                taskManagerStore.set_users(users);
            },
            onError: () => toast.error("Failed to load users"),
        }
    );

    // Use status from store directly - no duplicate state
    const selectedStatuses = (task_filters.status as TaskStatusType[]) || [];

    // For server-side pagination, display tasks directly from query
    const displayTasks = useMemo(() => {
        return taskListQuery.data?.tasks || [];
    }, [taskListQuery.data]);

    // // Reset to page 1 when filters change
    // useEffect(() => {
    //     setCurrentPage(1);
    // }, [
    //     task_filters.userList,
    //     task_filters.status,
    //     task_filters.pivLevel1,
    //     task_filters.surveys,
    // ]);

    // Status counts - use task summary from server if available
    const statusCounts = useMemo(() => {
        if (taskListQuery.data?.total) {
            const status = taskListQuery.data?.status || {};
            return {
                all: taskListQuery.data?.total || 0,
                "not-started": status.not_started || status["not-started"] || 0,
                "in-progress": status.in_progress || status["in-progress"] || 0,
                completed: status.completed || 0,
            };
        }
        return {
            all: 0,
            "not-started": 0,
            "in-progress": 0,
            completed: 0,
        };
    }, [taskListQuery.data]);

    // Handlers
    const handleViewTask = useCallback(
        (task: Task) => {
            setSelectedTask(task);
            setViewDrawerOpen(true);
            taskManagerStore.set_active_task(task);
        },
        [taskManagerStore]
    );

    const handleEditTask = useCallback(
        (task: Task) => {
            setSelectedTask(task);
            setEditDrawerOpen(true);
            taskManagerStore.set_active_task(task);
        },
        [taskManagerStore]
    );

    const handleCloseViewDrawer = useCallback(() => {
        setViewDrawerOpen(false);
        // setSelectedTask(null);
    }, []);

    const handleCloseEditDrawer = useCallback(() => {
        setEditDrawerOpen(false);
        setSelectedTask(null);
    }, []);

    const handleSwitchToEdit = useCallback(() => {
        setViewDrawerOpen(false);
        setEditDrawerOpen(true);
    }, []);

    const handleSwitchToView = useCallback(() => {
        setEditDrawerOpen(false);
        setViewDrawerOpen(true);
    }, []);

    const handleStatusCardClick = useCallback(
        (status: TaskStatusType) => {
            const isSameSelection = selectedStatuses.length === 1 && selectedStatuses[0] === status;
            const nextStatuses = isSameSelection ? [] : [status];

            taskManagerStore.update_task_filters({
                userList: task_filters.userList,
                pivLevel1: task_filters.pivLevel1,
                status: nextStatuses,
                surveys: task_filters.surveys,
            });
        },
        [
            selectedStatuses,
            taskManagerStore,
            task_filters.userList,
            task_filters.pivLevel1,
            task_filters.surveys,
        ]
    );

    const handleResetStatusFilters = useCallback(() => {
        taskManagerStore.update_task_filters({
            userList: task_filters.userList,
            pivLevel1: task_filters.pivLevel1,
            status: [],
            surveys: task_filters.surveys,
        });
    }, [taskManagerStore, task_filters.userList, task_filters.pivLevel1, task_filters.surveys]);

    const handleFiltersChange = useCallback(
        (filters: { userList: number[]; pivLevel1: string[]; surveys: any[] }) => {
            taskManagerStore.update_task_filters({
                userList: filters.userList,
                pivLevel1: filters.pivLevel1,
                status: task_filters.status, // Keep existing status filters
                surveys: filters.surveys,
            });
            setCurrentPage(1); // Reset to first page when filters change
        },
        [taskManagerStore, task_filters.status]
    );

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const handlePageSizeChange = useCallback(
        (size: number) => {
            setPageSize(size);
            setCurrentPage(1); // Reset to first page when page size changes
        },
        [pageSize]
    );

    const isTaskListLoading = taskListQuery.isLoading || (taskListQuery.isFetching && !taskListQuery.data);

    // if (projectLoading) return <LoadingState />;

    return (
        <TaskManagerErrorBoundary>
            <PageShell
                title="Task Manager"
                headerActions={
                    <div className="flex items-center gap-2">
                        {/* Assign Tasks Button */}
                        <Button
                            onClick={toggle_assign_task_modal}
                            startIcon={<i className="fas fa-users text-xs" />}
                            variant="outlined"
                            color="inherit"
                            size="medium"
                            className="hidden md:inline-flex"
                        >
                            Assign Tasks
                        </Button>

                        {/* Create Task Button */}
                        <Button
                            onClick={() => setCreateDrawerOpen(true)}
                            startIcon={<i className="fas fa-plus text-xs" />}
                            variant="contained"
                            color="primary"
                            size="medium"
                        >
                            Create Task
                        </Button>
                    </div>
                }
                breadcrumbs={<FTPBreadCrumbs crumbs={crumbs} />}
                contentPadding="px-4"
            >
                {/* Tabs + Filters + Table as one surface */}
                <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    {/* Status Tabs */}
                    <div className="border-b border-slate-200 px-4 py-3">
                        <div className="scrollbar-hide flex items-center gap-2 overflow-x-auto">
                            {STATUS_TABS.map((tab) => {
                                const count = tab.status ? statusCounts[tab.status] : statusCounts.all;
                                const isActive =
                                    (tab.status === null && selectedStatuses.length === 0) ||
                                    (tab.status !== null &&
                                        selectedStatuses.length === 1 &&
                                        selectedStatuses[0] === tab.status);

                                const handleClick = () => {
                                    if (tab.status === null) {
                                        handleResetStatusFilters();
                                    } else {
                                        handleStatusCardClick(tab.status);
                                    }
                                };

                                return (
                                    <button
                                        key={tab.key}
                                        onClick={handleClick}
                                        className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-3.5 py-2 text-sm font-semibold transition-all ${
                                            isActive ? tab.activeColor : tab.inactiveColor
                                        }`}
                                        aria-pressed={isActive}
                                    >
                                        <i
                                            className={`fas ${tab.icon} text-xs ${
                                                isActive ? tab.iconColor.replace("500", "600") : tab.iconColor
                                            }`}
                                        />
                                        {tab.label}
                                        <span
                                            className={`rounded-full border px-2 py-0.5 text-xs ${
                                                isActive ? tab.chipActive : tab.chip
                                            }`}
                                        >
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Filters & Search */}
                    <div className="border-b border-slate-200 px-4 py-3">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0 flex-1">
                                <InlineFilters onFiltersChange={handleFiltersChange} isIntegrated={true} />
                            </div>
                        </div>
                    </div>

                    {/* Task Table */}
                    <div className="px-2 pb-2 md:px-4">
                        <TaskTable
                            tasks={displayTasks}
                            isLoading={isTaskListLoading}
                            onTaskView={handleViewTask}
                            onTaskEdit={handleEditTask}
                            pageSize={pageSize}
                            currentPage={currentPage}
                            totalItems={taskListQuery.data?.total || taskManagerStore.task_list_total || 0}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            serverSidePagination={true}
                        />
                    </div>
                </div>
                <BoothMapView geoJsonUrl={geoJsonUrl} task={toJS(selectedTask)} />
            </PageShell>

            {/* Task View Drawer */}
            <TaskView
                isOpen={viewDrawerOpen}
                onClose={() => setViewDrawerOpen(false)}
                task={selectedTask}
                onEdit={handleSwitchToEdit}
                onRefresh={handleRefresh}
            />

            {/* Task Edit Drawer */}
            <TaskEdit
                isOpen={editDrawerOpen}
                onClose={handleCloseEditDrawer}
                task={selectedTask}
                onCancel={handleSwitchToView}
                onRefresh={handleRefresh}
            />

            {/* Task Create Drawer */}
            <TaskCreate
                isOpen={createDrawerOpen}
                onClose={() => setCreateDrawerOpen(false)}
                projectid={projectid}
                onRefresh={handleRefresh}
            />

            {/* Assign Tasks Overlay */}
            <SideOverlay
                show={show_assign_task_modal}
                onClose={toggle_assign_task_modal}
                title="Assign Tasks"
                size="large"
            >
                <AssignTaskForm />
            </SideOverlay>

            {/* Map View */}
            {/* {show_booth_map && active_task && (
                
            )} */}
        </TaskManagerErrorBoundary>
    );
});

export default TaskManager;
